import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, IMG } from "@/data/content";

export const Logo = ({ testId = "nav-logo" }) => (
  <Link to="/" className="flex items-center gap-3 group" data-testid={testId}>
    <span className="relative grid place-items-center w-9 h-9">
      <span className="absolute inset-0 rounded-full bg-[#6B2D8F]/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <img src={IMG.logo} alt="Habitreat" className="relative h-9 w-auto" />
    </span>
    <span className="font-display text-2xl tracking-wide text-slate-100">Habitreat</span>
  </Link>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open ? "bg-[#070A12]/85 backdrop-blur-xl border-[#C5A059]/20" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.id}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide link-underline pb-1 transition-colors duration-300 ${
                  isActive ? "text-[#E2C68C]" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {n.id === "immediate-support" && <span className="pulse-dot inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-2 align-middle" />}
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-book-consultation-button"
            className="btn-bronze hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-medium tracking-wide"
          >
            Book Consultation <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden w-11 h-11 grid place-items-center border border-[#C5A059]/30 text-slate-100"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-[#070A12] border-t border-[#C5A059]/15"
          >
            <div className="px-5 sm:px-8 py-6 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-link-${n.id}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-3xl border-b border-white/5 ${isActive ? "text-[#E2C68C]" : "text-slate-100"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/contact" data-testid="mobile-nav-book-button" className="btn-bronze mt-6 py-4 text-center text-sm font-medium">
                Book a Confidential Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
